import type { Chapter } from './types.js';

/**
 * The era palette, lifted from the `ERA` table in the crew-app prototype.
 * Shared so the phones, the cockpit and the TV agree on what an era looks like.
 */
export interface EraLook {
  tag: string;
  bg: string;
  fg: string;
  acc: string;
}

export const BOARDING_LOOK: EraLook = {
  tag: 'CHECK-IN',
  bg: '#0c1219',
  fg: '#dfe9f2',
  acc: '#4fd8c2',
};

export const JUMP_LOOK: EraLook = {
  tag: 'JUMPING',
  bg: '#120e26',
  fg: '#efeaff',
  acc: '#a08cff',
};

export const CHRONICLE_LOOK: EraLook = {
  tag: 'THE END',
  bg: '#12100a',
  fg: '#f3ead6',
  acc: '#d9b96a',
};

export const ERA_LOOKS: Record<1 | 2 | 3 | 4 | 5, EraLook> = {
  1: { tag: 'ERA 1 · EARTH · 2026', bg: '#0a1122', fg: '#e6efff', acc: '#6fb7ff' },
  2: { tag: 'ERA 2 · THE BOOM · 2213', bg: '#170b28', fg: '#f4e9ff', acc: '#ff4fd8' },
  3: { tag: 'ERA 3 · THE WAR · 2398', bg: '#1c0508', fg: '#ffe6e6', acc: '#ff2e3f' },
  4: { tag: 'ERA 4 · UNDERGROUND · 2617', bg: '#211609', fg: '#f0e2c8', acc: '#e08a3c' },
  5: { tag: 'ERA 5 · THE SURFACE · 2791', bg: '#e4dec9', fg: '#2c3527', acc: '#5c7d4e' },
};

export function lookFor(chapter: Chapter, phase: string): EraLook {
  if (phase === 'jump' || phase === 'charge') return JUMP_LOOK;
  if (phase === 'farewell') return CHRONICLE_LOOK;
  if (chapter === 0) return BOARDING_LOOK;
  return ERA_LOOKS[chapter];
}

/**
 * The five hero pours. One serve per chapter (03-SHOW-DATA.md), copy verbatim from
 * the crew-app prototype.
 *
 * `product` names a real bottle. That is a deliberate, informed choice — see
 * README non-negotiable 4 and the note beside it.
 */
export interface Serve {
  chapter: 1 | 2 | 3 | 4 | 5;
  index: 1 | 2 | 3 | 4 | 5;
  era: string;
  eraTitle: string;
  name: string;
  product: string;
  /**
   * What is in the glass, from the Bar Book (Marek, 2026-09-27): the line under the name on the table,
   * the phones and the printed menu. `product` stays the bottle, for the coasters and the cockpit.
   */
  ingredients: string;
  note: string;
}

export const SERVES: Record<1 | 2 | 3 | 4 | 5, Serve> = {
  1: {
    chapter: 1,
    index: 1,
    era: '2026',
    eraTitle: 'ERA 1 · EARTH',
    name: 'LAST HORIZON',
    product: 'No.3 London Dry Gin',
    ingredients: 'No.3 gin, green tea & pineapple water, rice mirin',
    note: 'The last familiar pour before departure. Clean, bright, exactly as you remember Earth.',
  },
  2: {
    chapter: 2,
    index: 2,
    era: '2213',
    eraTitle: 'ERA 2 · THE BOOM',
    name: 'NEON GARDEN',
    product: 'Roku Gin',
    ingredients: 'Roku gin, yuzu, Empirical Symphony 6, peach Monster',
    note: 'Six botanicals, grown in glass and light. People and machines built this together, and it works.',
  },
  3: {
    chapter: 3,
    index: 3,
    era: '2398',
    eraTitle: 'ERA 3 · THE WAR',
    name: 'EMBER PROTOCOL',
    product: 'The Macallan 12',
    ingredients: 'The Macallan 12, sparkling in its own bottle',
    note: 'Twelve years in oak. Older than the war that took everything above ground.',
  },
  4: {
    chapter: 4,
    index: 4,
    era: '2617',
    eraTitle: 'ERA 4 · UNDERGROUND',
    name: 'FAIR TRADE',
    product: 'Brugal 1888',
    ingredients: 'Brugal 1888, roasted coconut & white miso',
    note: 'Aged rum, traded hand to hand. Under the sand, warmth is the hardest currency.',
  },
  5: {
    chapter: 5,
    index: 5,
    era: '2791',
    eraTitle: 'ERA 5 · BACK ON THE SURFACE',
    name: 'FIRST SEED',
    product: 'No.3 London Dry Gin',
    ingredients: 'No.3 gin, soju, Skottgård gooseberry, lime, Thai basil foam',
    note: 'The bottle we left Earth with, back with what grew here: gooseberry and basil from the garden.',
  },
};

export function serveFor(chapter: Chapter): Serve | undefined {
  return chapter === 0 ? undefined : SERVES[chapter];
}

/** Where each jump is going, for the jump screen. */
export const DESTINATIONS: Record<2 | 3 | 4 | 5, { year: string; delta: string }> = {
  2: { year: '2213', delta: '+187 YEARS · THE BOOM' },
  3: { year: '2398', delta: '+185 YEARS · THE WAR' },
  4: { year: '2617', delta: '+219 YEARS · UNDERGROUND' },
  5: { year: '2791', delta: '+174 YEARS · BACK ON THE SURFACE' },
};

/**
 * The year to announce on a jump screen.
 *
 * Timing matters: `charge` runs at the END of a chapter, before the jump, so the
 * destination is the chapter ahead. `jump` runs AFTER `advanceChapter`, so the show
 * is already in the destination chapter and it is the current one. Getting this
 * backwards makes the TV announce the wrong year on the show's biggest beat.
 */
export function destinationYear(chapter: Chapter, phase: string): string {
  const target = phase === 'jump' || phase === 'intro' ? chapter : Math.min(5, chapter + 1);
  if (target <= 0) return SERVES[1].era;
  return SERVES[target as 1 | 2 | 3 | 4 | 5].era;
}

/** The chapter a jump screen is arriving at. */
export function destinationChapter(chapter: Chapter, phase: string): 1 | 2 | 3 | 4 | 5 {
  const target = phase === 'jump' || phase === 'intro' ? chapter : Math.min(5, chapter + 1);
  return (target <= 0 ? 1 : target) as 1 | 2 | 3 | 4 | 5;
}

/**
 * The plates. Food lands after the first, third and last cocktail — three courses
 * across five pours, so the table is never eating and jumping at once.
 *
 * The menu is Kristine's (2026-09-22). Her card had four plates; the dessert was dropped.
 * The dishes are the Bar Book's (Marek, 2026-09-27). The reindeer went to 2617 for a morning and
 * came back to the last pour, in 2791, the same day (Marek), so food lands in chapters 1, 3 and 5
 * and 2213 and 2617 have none. `pairing` is the pour it follows,
 * so the screen can say what it is arriving after. The menu card, the phones, the table and
 * the TV all read this table.
 */
export interface Course {
  /** The chapter it is served in. */
  chapter: 1 | 3 | 5;
  /** First, second or third plate of the night. */
  index: 1 | 2 | 3;
  /** The pour it follows. */
  pairing: string;
  name: string;
  dish: string;
  note: string;
  /** The line the room hears while it lands. */
  callout: string;
}

export const COURSES: Record<1 | 3 | 5, Course> = {
  // The dishes as the Bar Book has them (Marek, 2026-09-27).
  1: {
    chapter: 1,
    index: 1,
    pairing: 'Last Horizon',
    name: 'DILL & JUNIPER-CURED SALMON',
    dish: 'pickled Skottgård gooseberry, rye crisp — served ice-cold',
    note: 'Cold water, a northern fish, and the last plate cooked on Earth as it is tonight.',
    callout: 'FIRST PLATE · STILL ON EARTH · SERVED ICE-COLD',
  },
  // Fish, croquette, meat (Marek, 2026-09-25): the croquette is the war's plate, the reindeer the
  // first feast back on the surface.
  3: {
    chapter: 3,
    index: 2,
    pairing: 'Ember Protocol',
    name: 'POTATO & MUSHROOM TRIM CROQUETTE',
    dish: 'rye-crumb crust, tomato jam, pickled onion',
    note: 'Potatoes and mushrooms, the food of hard years: they keep, they fill you, and they grow where little else will.',
    callout: 'RATIONS ISSUED · EQUAL SHARES · NOBODY EATS FIRST',
  },
  5: {
    chapter: 5,
    index: 3,
    pairing: 'First Seed',
    name: 'JUNIPER-SMOKED REINDEER',
    dish: 'caramelized celeriac root, red wine sauce with cherry glaze, crispy shallot, rye',
    note: 'The herds are back on open land, and the first feast under the sky is cooked the old way, over juniper.',
    callout: 'THE SURFACE GROWS AGAIN · EAT WHAT IT GAVE BACK',
  },
};

export function courseFor(chapter: Chapter): Course | null {
  return chapter === 1 || chapter === 3 || chapter === 5 ? COURSES[chapter] : null;
}

/**
 * The last picture on Earth.
 *
 * Called from the TV in chapter 1, after the plate and before the crew charge the
 * drive. It is the only beat of the night that asks the room to look at each other
 * rather than at a screen, and it is the only one that cannot be repeated: after this
 * the ship is not on Earth any more.
 */
export const PHOTO_MOMENT = {
  kicker: 'PHONES DOWN · LOOK AT THE CAPTAIN',
  title: 'THE LAST PICTURE ON EARTH',
  // The moment is the captain's click, not a clock the guests keep (Marek, 2026-10-24: "when I press the button on the
  // clicker … it's time to take the picture now"): no countdown anywhere.
  line: 'Now — one picture before the SS Aurora leaves 2026 for good.',
  instruction: 'Put your phone down, squeeze in close and look at the captain. One photo of all of us — the last one on Earth.',
  footer: 'After the photo, your games open.',
  /** About how long the photo takes — for the test bench's tour only; nothing on the night counts it down. */
  holdMs: 90_000,
} as const;
